/*
 * Copyright (c) 2023 Pegasystems Inc.
 * All rights reserved.
 */

import constants, { publicConstants } from '../constants';
import { dispatch, getStore } from '../store/state/state-manager';
import * as RulesStore from '../rules/rules-store';
import AnnotationProcessor from '../annotation/processor';
import ContextProcessor from '../context/context-processor';
import * as CaseUtils from '../utils/case-utils';
import { ActionManagerInstance } from '../actions/manager';
import Utils from '../utils/utils';
import ContextTreeManager from '../contextree/treemanager';
import type { CalculatedField } from '../types';
import CaseInfoUtils from '../case/utils';
import ObjectTransformer from '../utils/object-transformer';
import type { DxAPIResponseObject } from '../case/types';
import { C11nEnv, createC11nEnv } from '../interpreter/c11n-env';
import type { ComponentMetadataConfig } from '../interpreter/types';
import type { PropertyModel } from '../rules/types';
import type { RefreshAPIOptions } from '../globals';
import ParagraphUtils from '../dynamic-content/util';

import { refreshData, fillFormWithAI } from './action-creator';
import type { RefreshPayLoad, RefreshPropertyChange, ReqConfig } from './types';

// Keeps track of the views with refresh targets.
export const refreshTargetViewsList: {
  [key: string]: { [key: string]: { [key: string]: string } };
} = {};

/**
 * Description : Removes stale container item from refreshList
 * @param context container name
 */
export const removeViewsFromTargetList = (context: string) => {
  if (context && refreshTargetViewsList[context]) {
    delete refreshTargetViewsList[context];
  }
};

/**
 * [dispatchRefreshData]
 * Description : Dispatches the REFRESH_DATA action
 * @param ID              CaseID or AssignmentID
 * @param actionID        Action ID either a casewide action or assignment action
 * @param context         Data context
 * @param caseWideAction  Whether the action is a case wide action or not.
 * @param options         options object for actions
 */
export const dispatchRefreshData = (
  ID: string,
  actionID: string,
  objectTypeID: string,
  context: string | null,
  caseWideAction: boolean,
  isObjectType: boolean,
  isExternalObject: boolean,
  options: RefreshAPIOptions
) => {
  if (!(ID && context && actionID)) {
    return;
  }

  const actionMgrID = options.actionMgrID || ActionManagerInstance.getNextActionMgrId();

  // payload to the REFRESH_DATA action
  const payload = {
    ID,
    actionID,
    objectTypeID,
    context,
    caseWideAction,
    isObjectType,
    isExternalObject,
    options: {
      ...options,
      actionMgrID
    }
  };
  if (options.isFillFormWithAI) {
    dispatch(fillFormWithAI(payload));
  } else {
    // Dispatching REFRESH_DATA
    dispatch(refreshData(payload));
  }
};

/**
 * [dispatchCaseViewRefresh]
 * Description : Dispatches the REFRESH_DATA action
 * @param ID              CaseID or AssignmentID
 * @param viewID          View ID
 * @param context         Data context
 * @param pageReference   page reference
 * @param options         Options object for action
 */
export const dispatchCaseViewRefresh = (
  ID: string,
  viewID: string,
  context: string,
  pageReference: string,
  options: RefreshAPIOptions
) => {
  if (!(ID && context && viewID)) {
    return;
  }

  if (pageReference) {
    pageReference = Utils.convertPageRefToClipboardPageNotation(pageReference);
  }

  // payload to the REFRESH_DATA action
  const payload: RefreshPayLoad = {
    ID,
    viewID,
    context,
    options,
    caseViewAction: true,
    interestPage: pageReference
  };
  // Dispatching REFRESH_DATA
  dispatch(refreshData(payload));
};

export const dispatchRefreshForEmbeddeddataWithAction = (
  caseID: string,
  assignmentID: string,
  actionID: string,
  context: string,
  interestPage: string,
  interestPageActionID: string,
  isLocalAction: boolean,
  isMultiRecordData: boolean,
  editType: string,
  options: RefreshAPIOptions = {}
) => {
  if (!((caseID || assignmentID) && context && actionID && interestPage)) {
    return;
  }

  if (interestPage) {
    interestPage = Utils.convertPageRefToClipboardPageNotation(interestPage);
  }

  // payload to the REFRESH_DATA action
  const payload: RefreshPayLoad = {
    ID: !isLocalAction && editType === constants.ACTION ? assignmentID : caseID,
    actionID,
    context,
    options,
    interestPage,
    interestPageActionID,
    isLocalAction,
    isMultiRecordData,
    editType
  };
  // Dispatching REFRESH_DATA
  dispatch(refreshData(payload));
};

/**
 * [recordViewForRefresh]
 * Description : Pushes the view to a list. This list holds all the views which have refresh targets.
 * @param viewName        view name from which the refresh is triggered
 * @param context         Data context
 * @param pageReference   Page reference inside the content
 */
export const recordViewForRefresh = (viewName: string | undefined, context: string, pageReference: string) => {
  if (!refreshTargetViewsList[context]) {
    refreshTargetViewsList[context] = {};
  }
  if (viewName && !refreshTargetViewsList[context][viewName]) {
    refreshTargetViewsList[context][viewName] = { pageReference };
  }
};

/**
 * [shouldViewRefresh]
 * Description : Returns true if context has any views present in it,else returns false.
 *               Refresh will happen for all the views present in that context.
 * @param context     Data context
 * @returns boolean
 */
export const shouldViewRefresh = (context: string) => {
  return (
    refreshTargetViewsList[context] &&
    Object.keys(refreshTargetViewsList[context]).length > 0 &&
    CaseUtils.isPerform(context)
  );
};

/**
 * This returns the leaf page name and property name of the embedded property
 * @param property with annotation
 * @returns containing pageName and propertyName
 * @example if we pass '@P .Address.Street' it will return { pageName : ".Address", propertyName : "Street"}
 */
const getPropertyReferenceInfo = (property: string) => {
  const pageName = property.substring(0, property.lastIndexOf('.'));
  const propertyName = property.substring(property.lastIndexOf('.') + 1);

  return { pageName, propertyName };
};

/**
 *This takes the property reference value as input and returns the isDeclarativeTarget value from property metadata
 * @param component C11nEnv object
 * @param paramValue data source parameter value
 * @returns isDeclarativeTarget
 *
 */
const extractDeclarativeTargetFromParam = (
  component: C11nEnv,
  paramValue: string,
  currentClassId?: string
): boolean => {
  const propertyReferenceObj = getPropertyReferenceInfo(paramValue.substring(paramValue.lastIndexOf(' ') + 1));
  const paramMetadata = RulesStore.getPropertyMetadata(
    propertyReferenceObj.propertyName,
    currentClassId || component.getValue(constants.CLASS_ID, propertyReferenceObj.pageName)
  ) as PropertyModel;
  return !!paramMetadata?.isDeclarativeTarget;
};

/**
 * This function takes c11Env object and datasource parameters and returns whether parameters are
 * declarative expression or not
 * @param component C11nEnv object
 * @param parameters datasource parameters can be object/array
 * @returns isDeclarativeTarget boolean value
 */
export const checkDataSourceParamForDeclarativeTargets = (
  component: C11nEnv,
  parameters: { [key: string]: string }[] | { [key: string]: string },
  currentClassId?: string
) => {
  let viewShouldRefresh = false;
  if (Array.isArray(parameters)) {
    const checkForDeclarativeTargets = (param: { [key: string]: string }) => {
      const paramValue = param.value;
      if (paramValue.startsWith('@P')) {
        viewShouldRefresh = extractDeclarativeTargetFromParam(component, paramValue, currentClassId);
      }
      return viewShouldRefresh;
    };
    parameters.some(checkForDeclarativeTargets);
  } else if (typeof parameters === 'object') {
    for (const key of Object.keys(parameters)) {
      if (parameters[key].startsWith('@P')) {
        viewShouldRefresh = extractDeclarativeTargetFromParam(component, parameters[key], currentClassId);
        if (viewShouldRefresh) break;
      }
    }
  }
  return viewShouldRefresh;
};

const checkPropMetaData = (component: C11nEnv, currentClassId?: string) => {
  const prop = component.getRawMetadata()?.config?.value;
  let viewShouldRefresh = false;
  if (prop) {
    const propMetadata = RulesStore.getPropertyMetadata(
      AnnotationProcessor.getLeafPropertyName(prop),
      currentClassId || ContextProcessor.resolveClassID(prop, component.getPageReference(), component.getContextName())
    ) as PropertyModel;
    // view data should be refreshed if any component in the view is a declare target.
    viewShouldRefresh = !!propMetadata?.isDeclarativeTarget;

    //  view data should be refreshed if decision paragraph is used for additionalInformation
    if (!viewShouldRefresh && propMetadata?.additionalInformation) {
      const paragraphName = AnnotationProcessor.trimAnnotation(propMetadata?.additionalInformation);
      viewShouldRefresh = ParagraphUtils.isDecisionParagraph(paragraphName, propMetadata.classID);
    }

    if (!viewShouldRefresh && propMetadata?.datasource?.parameters) {
      viewShouldRefresh = checkDataSourceParamForDeclarativeTargets(
        component,
        propMetadata?.datasource?.parameters as unknown as { [key: string]: string }[] | { [key: string]: string },
        currentClassId
      );
    }
    // picklist of DECISION type source
    if (!viewShouldRefresh) {
      viewShouldRefresh = propMetadata?.datasource?.tableType === constants.DECISION;
    }
  }

  return viewShouldRefresh;
};

/**
 * [inspectComponentForRefreshTargets]
 * Description : Inspects the component, checks if there are any refresh targets in it.
 * If any refresh targets, calls a method which tracks the view for further processing.
 * @param component   C11nEnv object
 */
export const inspectComponentForRefreshTargets = (component: C11nEnv) => {
  const viewShouldRefresh = component.isWhenExist() || checkPropMetaData(component);

  // View should be refreshed only if any of its components has when authored or is a declare target
  if (viewShouldRefresh) {
    recordViewForRefresh(component.getCurrentView(), component.getContextName(), component.getPageReference());
  }
};
/**
 * [inspectChildrenForRefreshTargets]
 * Description : Inspects the component children, checks if there are any refresh targets in it.
 * If any refresh targets, calls a method which tracks the view for further processing.
 * @param component   C11nEnv object
 */
export const inspectChildrenForRefreshTargets = (component: C11nEnv): void => {
  function recursiveParse(contextClass: string, childrens: ComponentMetadataConfig[]): boolean {
    for (const child of childrens) {
      const childComponent = createC11nEnv({
        meta: child
      }).getPConnect();
      const shouldRefresh = childComponent.isWhenExist() || checkPropMetaData(childComponent, contextClass);
      if (shouldRefresh) {
        return true;
      }
      if (child.children && recursiveParse(contextClass, child.children as ComponentMetadataConfig[])) {
        return true;
      }
    }
    return false;
  }
  let viewShouldRefresh = false;
  const config = component?.getRawMetadata()?.config;
  if (config?.type && config.type === 'multirecordlist' && config?.contextClass) {
    viewShouldRefresh = recursiveParse(config.contextClass, (config.children as ComponentMetadataConfig[]) ?? []);
  }
  if (viewShouldRefresh) {
    recordViewForRefresh(component.getCurrentView(), component.getContextName(), component.getPageReference());
  }
};

export const isParentObjectType = (context: string | null): boolean => {
  if (!context) {
    return false;
  }
  const stateData = getStore()?.getState()?.data?.[context];
  // added safe check to read casetype from either caseInfo or objectInfo
  const caseType = stateData?.caseInfo?.caseTypeID || stateData?.objectInfo?.objectTypeID;
  return CaseInfoUtils.isObjectCaseType(caseType);
};

export const refreshAssignment = (context: string | null, pageReference: string, options: RefreshAPIOptions) => {
  // Determine if it's a case-wide action or an object-specific action
  const caseWideAction = CaseUtils.isCaseWideAction(context, pageReference);
  const isObjectType =
    CaseInfoUtils.isObjectCaseType(options?.classID) || pageReference.startsWith(constants.OBJECT_INFO.OBJECT_INFO);
  let actionID: string;
  let ID: string;
  let objectTypeID = '';
  let isExternalObject = false;
  // Helper function to retrieve property value
  const getProperty = (property: string): string => {
    return ContextProcessor.getPropertyValue(property, pageReference, context) || '';
  };
  // Get actionID
  if (isObjectType && isParentObjectType(context)) {
    // Object-specific logic
    objectTypeID = getProperty(publicConstants.OBJECT_INFO.OBJECT_INFO_CONTENT_CLASS_ID);
    isExternalObject = CaseInfoUtils.isExternalObject(objectTypeID) as boolean;

    // Mark it as an object-wide action
    options.isObjectWideAction = true;

    // Get actionID for object
    actionID = getProperty(publicConstants.OBJECT_INFO.ACTIVE_ACTION_ID) || constants.VIEW_NAMES.OBJECT_CREATE_VIEW;

    // ID is either the case info ID or the object type ID
    ID = getProperty(constants.getProperty(publicConstants.CASE_INFO.CASE_INFO_ID)) || objectTypeID;
  } else {
    // Case-specific logic
    actionID =
      getProperty(publicConstants.CASE_INFO.ACTIVE_ACTION_ID) ||
      getProperty(publicConstants.CASE_INFO.ASSIGNMENTACTION_ID);

    if (caseWideAction) {
      // Get the case info ID for case-wide actions
      ID = getProperty(constants.getProperty(publicConstants.CASE_INFO.CASE_INFO_ID));
    } else {
      // Get assignment ID or fallback to context assignment ID
      const assignmentID = getProperty(constants.getProperty(publicConstants.CASE_INFO.ASSIGNMENT_ID));
      ID = assignmentID || getProperty(constants.getProperty(publicConstants.CONTEXT_DATA_ASSIGNMENTID));
    }
  }

  dispatchRefreshData(ID, actionID, objectTypeID, context, caseWideAction, isObjectType, isExternalObject, options);
};

/**
 * [refreshOnPropertyChange]
 * Description : This function gets the action type, action/assignment ID and gives a call to dispatch REFRESH_DATA
 * @param payload
 *          payload.context - Data context
 *          payload.pageReference - Page reference in the content
 */
export const refreshOnPropertyChange = (payload: RefreshPropertyChange) => {
  const { context, pageReference } = payload;

  const viewsTobeRefreshed = shouldViewRefresh(context);
  // Dispatch action to refresh the data only if any views are tracked for refresh.
  if (!viewsTobeRefreshed) {
    return;
  }

  const modalView = ContextProcessor.getPropertyValue(
    constants.CONTEXT_DATA_MODAL_VIEW.CONTEXT_DATA_MODAL_VIEW,
    pageReference,
    context
  );
  const { isMultiRecordData, editType } = modalView || {};

  if (isMultiRecordData) {
    const caseID = ContextProcessor.getPropertyValue(publicConstants.CASE_INFO.CASE_INFO_ID, pageReference, context);
    const assignmentID = ContextProcessor.getPropertyValue(
      publicConstants.CASE_INFO.ASSIGNMENT_ID,
      pageReference,
      context
    );
    const { interestPage, interestPageActionID, actionID, isLocalAction, viewName: viewID } = modalView;
    if (editType === constants.ACTION) {
      dispatchRefreshForEmbeddeddataWithAction(
        caseID,
        assignmentID,
        actionID,
        context,
        interestPage,
        interestPageActionID,
        isLocalAction,
        isMultiRecordData,
        editType
      );
    } else {
      dispatchCaseViewRefresh(caseID, viewID, context, interestPage, {});
    }
  } else {
    refreshAssignment(context, pageReference, {});
  }
};

export const appendQueryParamsForRefreshAPI = (payload: RefreshPayLoad, restApi?: string) => {
  const { requestType } = payload;
  if (payload?.options?.refreshFor && requestType === 'refresh') {
    const refreshFor = payload.options.refreshFor.replace(/\[(\d+)\]/g, (_, index) => {
      return `(${parseInt(index, 10) + 1})`;
    });
    restApi += `?refreshFor=${refreshFor}`;
  }
  if (payload?.options?.isFillFormWithAI && requestType === 'refresh') {
    restApi += '?fillFormWithAI=true';
  }
  return restApi;
};

const areRequestsIdentical = (existingRequest: ReqConfig, incomingRequest: ReqConfig): boolean => {
  const sameUrl = existingRequest.url === incomingRequest.url;
  const sameInterestPage = existingRequest.interestPage === incomingRequest.interestPage;
  return sameUrl && sameInterestPage;
};

export const shouldCancelRequest = (existingRequest: ReqConfig, incomingRequest: ReqConfig): boolean | string => {
  const isExistingRefreshFor = existingRequest.url?.includes('/refresh?refreshFor=');
  const isIncomingRefreshFor = incomingRequest.url?.includes('/refresh?refreshFor=');
  const isExistingRefresh = existingRequest.url?.endsWith('/refresh') && !isExistingRefreshFor;
  const isIncomingRefresh = incomingRequest.url?.endsWith('/refresh') && !isIncomingRefreshFor;

  if (isExistingRefresh && isIncomingRefresh) {
    return areRequestsIdentical(existingRequest, incomingRequest);
  }

  if (isExistingRefresh && isIncomingRefreshFor) {
    return true;
  }

  if (isExistingRefreshFor) {
    if (isIncomingRefresh) {
      return 'CANCEL_REFRESH';
    }

    if (isIncomingRefreshFor) {
      return false;
    }
  }

  return false;
};

export const triggerTransformCaseToObject = (
  response: DxAPIResponseObject,
  classID: string,
  isObjectType: boolean
): DxAPIResponseObject => {
  const shouldTransform = CaseInfoUtils.isObjectCaseType(classID) || isObjectType;

  if (!shouldTransform) {
    return response;
  }

  const objectTypeID = Utils.getIn(response?.data, publicConstants.OBJECT_INFO.OBJECT_INFO_CONTENT_CLASS_ID);
  const isExternalObject = CaseInfoUtils.isExternalObject(objectTypeID) as boolean;

  return isExternalObject ? response : ObjectTransformer.transformCaseData(response);
};

/**
 * This API will return the Fields which are configured with declarative target in the specified context.
 * @param contextName - name of the context.
 * @returns {CalculatedField[]} - returns an Array of objects.
 */
const getDeclarativeTargetFields = (contextName: string): CalculatedField[] => {
  const calculatedFields: CalculatedField[] = [];
  const declarativeFields = ContextTreeManager.getDeclarativeTargets(contextName);
  declarativeFields.forEach(({ name, pageReference }) => {
    const pageReferenceSplitArray = pageReference.split('.');
    pageReferenceSplitArray.shift();
    let context = pageReferenceSplitArray.join('.');
    if (name && context) {
      context = Utils.convertPageRefToClipboardPageNotation(context);
      calculatedFields.push({
        name,
        context
      });
    }
  });
  return calculatedFields;
};

/**
 * This API will return the Fields and Views which are configured with WHEN condition in the specified context.
 * @param contextName
 * @returns {CalculatedField[]} - returns an Array of objects.
 */
const getWhens = (contextName: string): CalculatedField[] => {
  const calculatedFields: CalculatedField[] = [];
  const whens = ContextTreeManager.getWhens(contextName);
  whens.forEach(({ name, type, pageReference }) => {
    const pageReferenceSplitArray = pageReference.split('.');
    pageReferenceSplitArray.shift();
    let context = pageReferenceSplitArray.join('.');
    if (name && context) {
      context = Utils.convertPageRefToClipboardPageNotation(context);
      calculatedFields.push({
        name,
        context,
        type
      });
    }
  });
  return calculatedFields;
};

/**
 * This API will returns fields which configured with declarative target, field and views which are
 * configured with when condition in the specified context.
 * @param contextName - name of the context.
 * @returns
 */
export const getItemsToRecalculate = (contextName: string) => {
  const fieldsWithDeclarativeTargets: CalculatedField[] = getDeclarativeTargetFields(contextName);
  const whens: CalculatedField[] = getWhens(contextName);
  return { fieldsWithDeclarativeTargets, whens };
};
